import type { CaptureResult } from 'posthog-js';
import { env } from '@/env';
import { screenFromPathname } from './screens';

// Static context computed once. Injected on every event via before_send (rather
// than posthog.register in `loaded`) so it's guaranteed on the FIRST event of a
// session too — including a first-paint $exception, the crash case this integration
// exists for, which a register-in-loaded call can race.
const appVersion = env.NEXT_PUBLIC_GIT_COMMIT_SHA;
const environment =
  env.NODE_ENV === 'production'
    ? env.NEXT_PUBLIC_WEB_BASE_URL.includes('staging')
      ? 'staging'
      : 'production'
    : 'development';

// PostHog `before_send` hook: runs synchronously on every event right before send,
// reading the current URL — so the normalised `screen` (+ ids) and the static
// super-properties land on the auto $pageview/$pageleave and every other event with
// no React-effect timing lag. Also the natural home for a future PII redaction pass.
// Must never throw: returning the event unchanged is always safe; null would drop it.
export const enrichWithScreen = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return event;

  // Session-replay snapshots are high-frequency and carry a large $snapshot_data
  // payload — don't spread/enrich them (pure overhead + noise).
  if (event.event === '$snapshot') return event;

  // before_send can run outside a browser context (e.g. worker); skip explicitly so
  // the catch below only ever hides a real mapping bug, not the no-window path.
  if (typeof window === 'undefined') {
    event.properties = { ...event.properties, appVersion, environment };
    return event;
  }

  try {
    const { screen, context } = screenFromPathname(window.location.pathname);
    event.properties = { ...event.properties, screen, ...context, appVersion, environment };
  } catch {
    event.properties = { ...event.properties, appVersion, environment };
  }

  return event;
};
